import React, {useEffect, useState} from "react";
import {
    Box, Button, Divider, FormControl, Grid, InputLabel,
    MenuItem, Pagination, Paper, Select, Stack,
    TextField, Tooltip, Typography
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import {Dayjs} from "dayjs";
import {ComplaintTable} from "../../components/internal-affairs/InternalAffairsComplaintMasterList";
import {useSearchParams} from "react-router-dom";
import {InternalAffairsArchivePageinationResponse} from "../../data/internal-affairs/InternalAffairsArchivePageinationResponse";
import {internalAffairsComplaintClassification} from "../../data/internal-affairs/InternalAffairsComplaintClassification";
import {internalAffairsComplaintOutcome} from "../../data/internal-affairs/InternalAffairsComplaintOutcome";
import QuestionMarkOutlinedIcon from "@mui/icons-material/QuestionMarkOutlined";

interface ArchiveFilters {
    query: string
    complainant: string
    outcome: string
    classification: string
    dateFrom: Dayjs | null
    dateTo: Dayjs | null
}

const empty: ArchiveFilters = {query: "", complainant: "", outcome: "", classification: "", dateFrom: null, dateTo: null}

function buildUrl(filters: ArchiveFilters, page: number): string {
    const p = new URLSearchParams()
    p.set("page", String(page))
    if (filters.query)          p.set("query", filters.query)
    if (filters.complainant)    p.set("complainant", filters.complainant)
    if (filters.outcome)        p.set("outcome", filters.outcome)
    if (filters.classification) p.set("classification", filters.classification)
    if (filters.dateFrom)       p.set("dateFrom", filters.dateFrom.startOf("day").toISOString())
    if (filters.dateTo)         p.set("dateTo", filters.dateTo.endOf("day").toISOString())
    return `/api/internal-affairs/archive/search?${p.toString()}`
}

function isFiltering(f: ArchiveFilters) {
    return f.query !== "" || f.complainant !== "" || f.outcome !== "" || f.classification !== "" || f.dateFrom !== null || f.dateTo !== null
}

export const InternalAffairsArchivedComplaintsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [page, setPage] = useState(parseInt(searchParams.get("page") ?? "1"))
    const [filters, setFilters] = useState<ArchiveFilters>(empty)
    const [complaintArchive, setComplaintArchive] = useState<InternalAffairsArchivePageinationResponse>({
        totalComplaints: 0, totalPages: 1, complaints: []
    })

    useEffect(() => { setPage(1) }, [filters])

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(buildUrl(filters, page))
                setComplaintArchive(await response.json())
            } catch (e) {
                console.error(e)
            }
        }
        fetchData()
    }, [page, filters])

    const handleChangePage = (_: React.ChangeEvent<unknown>, value: number) => {
        setPage(value)
        searchParams.set("page", String(value))
        setSearchParams(searchParams)
    }

    return (
        <Stack spacing={1}>
            <Grid container spacing={2} alignItems="flex-start">

                {/* results */}
                <Grid item xs={12} md={9}>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <ComplaintTable complaints={complaintArchive.complaints}/>
                        <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: "block"}}>
                            {complaintArchive.totalComplaints} result{complaintArchive.totalComplaints !== 1 ? "s" : ""}
                        </Typography>
                    </Paper>
                    <Box sx={{display: "flex", justifyContent: "center", mt: 2}}>
                        <Pagination color="secondary" count={complaintArchive.totalPages} page={page} onChange={handleChangePage}/>
                    </Box>
                </Grid>

                {/* filter panel */}
                <Grid item xs={12} md={3}>
                    <Paper variant="outlined" sx={{p: 2}}>
                        <Stack spacing={2}>
                            <Typography variant="subtitle2">Filters</Typography>
                            <Divider/>

                            <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                <TextField
                                    label="Search defendant / ID"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={filters.query}
                                    onChange={e => setFilters({...filters, query: e.target.value})}
                                />
                                <Tooltip title="Search by defendant steam name, RP name, community ID, or complaint display ID">
                                    <QuestionMarkOutlinedIcon sx={{fontSize: 18, color: "text.secondary", flexShrink: 0}}/>
                                </Tooltip>
                            </Box>

                            <TextField
                                label="Complainant"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={filters.complainant}
                                onChange={e => setFilters({...filters, complainant: e.target.value})}
                                helperText="Steam name, RP name or community ID"
                            />

                            <FormControl size="small" fullWidth>
                                <InputLabel>Outcome</InputLabel>
                                <Select
                                    value={filters.outcome}
                                    label="Outcome"
                                    onChange={e => setFilters({...filters, outcome: e.target.value})}
                                >
                                    <MenuItem value=""><em>Any</em></MenuItem>
                                    {internalAffairsComplaintOutcome
                                        .filter(o => o.key !== "pending")
                                        .map(o => <MenuItem key={o.key} value={o.key.toUpperCase()}>{o.displayName}</MenuItem>)}
                                </Select>
                            </FormControl>

                            <FormControl size="small" fullWidth>
                                <InputLabel>Classification</InputLabel>
                                <Select
                                    value={filters.classification}
                                    label="Classification"
                                    onChange={e => setFilters({...filters, classification: e.target.value})}
                                >
                                    <MenuItem value=""><em>Any</em></MenuItem>
                                    {internalAffairsComplaintClassification
                                        .filter(c => c.key !== "pending" && c.key !== "no_evidence")
                                        .map(c => <MenuItem key={c.key} value={c.key.toUpperCase()}>{c.displayName}</MenuItem>)}
                                </Select>
                            </FormControl>

                            <DatePicker
                                label="Date from"
                                value={filters.dateFrom}
                                onChange={v => setFilters({...filters, dateFrom: v})}
                                slotProps={{textField: {size: "small", fullWidth: true}}}
                                views={["year", "month", "day"]}
                                format="DD/MM/YYYY"
                            />
                            <DatePicker
                                label="Date to"
                                value={filters.dateTo}
                                onChange={v => setFilters({...filters, dateTo: v})}
                                slotProps={{textField: {size: "small", fullWidth: true}}}
                                views={["year", "month", "day"]}
                                minDate={filters.dateFrom ?? undefined}
                                format="DD/MM/YYYY"
                            />

                            {isFiltering(filters) && (
                                <Button size="small" color="warning" onClick={() => setFilters(empty)}>
                                    Clear filters
                                </Button>
                            )}
                        </Stack>
                    </Paper>
                </Grid>

            </Grid>
        </Stack>
    )
}

